import { MarkerType } from "reactflow";
import { findNodeStats } from "./Graph/GraphNodeUtils";

export const processData = (
  data,
  hierarchyStats = null,
  showNodesWithNoValue,
  layoutDirection = "vertical",
) => {
  let nodeId = 0;
  const rootNodeId = `node_${nodeId++}`;

  // Constants for layout - adjust based on direction
  // When horizontal: nodes flow left-to-right, so we need more vertical spacing
  // When vertical: nodes flow top-to-bottom, so we need more horizontal spacing
  // COMPACT LAYOUT: Significantly reduced spacing values for space efficiency
  const HORIZONTAL_SPACING = layoutDirection === "horizontal" ? 200 : 250; // Reduced from 400/600
  const VERTICAL_SPACING = layoutDirection === "horizontal" ? 180 : 150; // Reduced from 600/350
  const VALUE_HORIZONTAL_OFFSET = layoutDirection === "horizontal" ? 60 : 80; // Reduced from 120/160
  const ROOT_Y_OFFSET = layoutDirection === "horizontal" ? 0 : -100; // Reduced from -200
  const ROOT_X_OFFSET = layoutDirection === "horizontal" ? -100 : 0; // Reduced from -200

  // DYNAMIC SPACING: Calculate based on tree structure
  const calculateDynamicSpacing = (nodeCount, level) => {
    // Reduce spacing for levels with fewer nodes
    const baseFactor = Math.min(1, nodeCount / 5); // Scale down if less than 5 nodes
    const depthFactor = Math.max(0.7, 1 - level * 0.1); // Reduce spacing at deeper levels
    return baseFactor * depthFactor;
  };

  // Initialize nodes with root at center position
  const nodes = [
    {
      id: rootNodeId,
      type: "root",
      position: {
        x: ROOT_X_OFFSET,
        y: ROOT_Y_OFFSET,
      }, // Let ReactFlow's fitView handle centering
      data: {
        label: "Root",
        nodePath: [],
      },
    },
  ];
  const edges = [];

  // Track min and max positions for both axes
  let minX = ROOT_X_OFFSET;
  let maxX = ROOT_X_OFFSET;
  let minY = ROOT_Y_OFFSET;
  let maxY = ROOT_Y_OFFSET;

  // Rest of the tracking variables
  const levelRightmostX = new Map();
  const subtreeWidths = new Map();

  // First pass: Calculate subtree widths
  const calculateSubtreeWidth = (node, depth = 0) => {
    if (!node || typeof node !== "object") return 0;

    let width = 0;
    const isQuestion = depth % 2 === 0;

    if (isQuestion) {
      // For question nodes, sum up widths of all children
      Object.entries(node).forEach(([key, value]) => {
        if (key !== "TagAvailableValues") {
          width += calculateSubtreeWidth(value, depth + 1);
        }
      });
      // Use more compact minimum width
      width = Math.max(
        width,
        layoutDirection === "horizontal"
          ? VERTICAL_SPACING * 0.8
          : HORIZONTAL_SPACING * 0.8,
      ); // Minimum width reduced
    } else {
      // For value level, count available values
      const valueCount = node.TagAvailableValues?.length || 1;
      // Apply dynamic spacing factor
      const spacingFactor = calculateDynamicSpacing(
        valueCount,
        Math.floor(depth / 2),
      );
      width =
        valueCount *
        (layoutDirection === "horizontal"
          ? VERTICAL_SPACING * spacingFactor
          : (HORIZONTAL_SPACING + VALUE_HORIZONTAL_OFFSET) * spacingFactor);
    }

    subtreeWidths.set(nodeId, width);
    return width;
  };

  // Calculate initial subtree widths
  calculateSubtreeWidth(data);

  const processNode = (
    node,
    depth = 0,
    parentId = null,
    parentX = 0,
    parentY = 0,
    currentPath = [],
  ) => {
    // If node is null, undefined, or empty object, just return without processing children
    if (!node || typeof node !== "object" || Object.keys(node).length === 0) {
      return;
    }

    const isQuestion = depth % 2 === 0;

    if (!isQuestion) {
      const availableValues = Array.isArray(node.TagAvailableValues)
        ? node.TagAvailableValues
        : [];

      if (node.TagAvailableValues !== undefined) {
        // In tag-value view, only show value nodes that exist in the graph
        // These are the "active" values that will be used for extraction
        let values = Object.keys(node)
          .filter((k) => k !== "TagAvailableValues")
          .sort();

        // if hierarchyStats is provided, filter out values that have a file_count of 0 if showNodesWithNoValue is true
        if (hierarchyStats && !showNodesWithNoValue) {
          const nodeStats = findNodeStats(hierarchyStats, currentPath);
          if (nodeStats?.children) {
            values = values.filter((value) =>
              nodeStats.children.some(
                (stat) => stat.name === value && stat.file_count > 0,
              ),
            );
          }
        }

        // Apply dynamic spacing based on number of values
        const dynamicSpacingFactor = calculateDynamicSpacing(
          values.length,
          Math.floor(depth / 2),
        );
        const effectiveVerticalSpacing =
          VERTICAL_SPACING * dynamicSpacingFactor;
        const effectiveHorizontalSpacing =
          HORIZONTAL_SPACING * dynamicSpacingFactor;

        const totalWidth =
          values.length *
          (layoutDirection === "horizontal"
            ? effectiveVerticalSpacing
            : effectiveHorizontalSpacing);

        let startPos;
        if (layoutDirection === "horizontal") {
          startPos = parentY - totalWidth / 2 + effectiveVerticalSpacing / 2;
        } else {
          startPos = parentX - totalWidth / 2 + effectiveHorizontalSpacing / 2;
        }

        values.forEach((value, index) => {
          let xPos, yPos;

          if (layoutDirection === "horizontal") {
            xPos = depth * HORIZONTAL_SPACING;
            yPos = startPos + index * effectiveVerticalSpacing;
          } else {
            xPos = startPos + index * effectiveHorizontalSpacing;
            yPos = depth * VERTICAL_SPACING;
          }

          // Track min and max positions
          minX = Math.min(minX, xPos);
          maxX = Math.max(maxX, xPos);
          minY = Math.min(minY, yPos);
          maxY = Math.max(maxY, yPos);

          const valueNodeId = `node_${nodeId++}`;
          const valuePath = [...currentPath, value];

          nodes.push({
            id: valueNodeId,
            type: "value",
            position: {
              x: xPos,
              y: yPos,
            },
            data: {
              id: valueNodeId,
              label: value,
              isOutOfSync: !availableValues.includes(value),
              nodePath: valuePath,
              parentId,
            },
          });

          levelRightmostX.set(
            depth,
            Math.max(
              levelRightmostX.get(depth) || 0,
              layoutDirection === "horizontal"
                ? yPos + VERTICAL_SPACING * dynamicSpacingFactor
                : xPos + HORIZONTAL_SPACING * dynamicSpacingFactor,
            ),
          );

          if (parentId) {
            edges.push({
              id: `edge_${parentId}_${valueNodeId}`,
              source: parentId,
              target: valueNodeId,
              type: "step",
              style: {
                opacity: 1,
                strokeOpacity: 1,
              },
            });
          }

          // Process child nodes
          if (value in node) {
            processNode(
              node[value],
              depth + 1,
              valueNodeId,
              xPos,
              yPos,
              valuePath,
            );
          }
        });

        // Only add "Not found" node for non-binary tag values (not yes/no)
        const lowerCaseValues = availableValues.map((value) =>
          value.toLowerCase(),
        );
        const isBinaryYesNo =
          lowerCaseValues.includes("yes") &&
          lowerCaseValues.includes("no") &&
          availableValues.length === 2;

        // Skip for binary yes/no values
        if (isBinaryYesNo) return;

        const notFoundNodeId = `node_${nodeId++}`;
        let notFoundX, notFoundY;

        if (layoutDirection === "horizontal") {
          notFoundX = depth * HORIZONTAL_SPACING;
          notFoundY =
            startPos + availableValues.length * effectiveVerticalSpacing;
        } else {
          notFoundX =
            startPos + availableValues.length * effectiveHorizontalSpacing;
          notFoundY = depth * VERTICAL_SPACING;
        }

        nodes.push({
          id: notFoundNodeId,
          type: "value",
          position: {
            x: notFoundX,
            y: notFoundY,
          },
          data: {
            id: notFoundNodeId,
            label: "Not found",
            isOutOfSync: false,
            nodePath: [...currentPath, "Not found"],
            parentId,
          },
        });
        edges.push({
          id: `edge_${parentId}_${notFoundNodeId}`,
          source: parentId,
          target: notFoundNodeId,
          type: "step",
          style: {
            opacity: 1,
            strokeOpacity: 1,
          },
        });
      }
    } else {
      Object.entries(node).forEach(([key, value], index) => {
        if (key === "TagAvailableValues") return;

        const currentId = `node_${nodeId++}`;
        const newPath = [...currentPath, key];

        // Calculate dynamic subtree width
        const nodeCount = Object.keys(node).filter(
          (k) => k !== "TagAvailableValues",
        ).length;
        const dynamicFactor = calculateDynamicSpacing(
          nodeCount,
          Math.floor(depth / 2),
        );

        const subtreeWidth =
          (subtreeWidths.get(nodeId) ||
            (layoutDirection === "horizontal"
              ? VERTICAL_SPACING
              : HORIZONTAL_SPACING)) * dynamicFactor;

        let xPos, yPos;

        if (layoutDirection === "horizontal") {
          xPos = depth * HORIZONTAL_SPACING;
          yPos =
            parentY +
            index * subtreeWidth -
            ((nodeCount - 1) * subtreeWidth) / 2;
        } else {
          xPos =
            parentX +
            index * subtreeWidth -
            ((nodeCount - 1) * subtreeWidth) / 2;
          yPos = depth * VERTICAL_SPACING;
        }

        // Track min and max positions
        minX = Math.min(minX, xPos);
        maxX = Math.max(maxX, xPos);
        minY = Math.min(minY, yPos);
        maxY = Math.max(maxY, yPos);

        nodes.push({
          id: currentId,
          type: "question",
          position: {
            x: xPos,
            y: yPos,
          },
          data: {
            id: currentId,
            label: key,
            nodePath: newPath,
            parentId,
          },
        });

        levelRightmostX.set(
          depth,
          Math.max(
            levelRightmostX.get(depth) || 0,
            layoutDirection === "horizontal"
              ? yPos + VERTICAL_SPACING * dynamicFactor
              : xPos + HORIZONTAL_SPACING * dynamicFactor,
          ),
        );

        edges.push({
          id: `edge_${parentId || rootNodeId}_${currentId}`,
          source: parentId || rootNodeId,
          target: currentId,
          type: "smoothstep",
          sourceHandle: layoutDirection === "horizontal" ? "right" : "bottom",
          targetHandle: layoutDirection === "horizontal" ? "left" : "top",
          style: {
            strokeWidth: 2,
            opacity: 1,
            strokeOpacity: 1,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 10,
            height: 10,
          },
        });

        if (value && typeof value === "object") {
          processNode(value, depth + 1, currentId, xPos, yPos, newPath);
        }
      });
    }
  };

  processNode(data, 0, null, ROOT_X_OFFSET, ROOT_Y_OFFSET);

  // Calculate the center point and offset needed
  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;
  const centerOffsetX = -graphWidth / 2 - minX;
  const centerOffsetY = -graphHeight / 2 - minY;

  // Apply the centering offset to all nodes
  nodes.forEach((node) => {
    node.position.x += centerOffsetX;
    node.position.y += centerOffsetY;
  });

  // Apply compact layout optimization
  optimizeCompactLayout(nodes, edges, layoutDirection);

  return { nodes, edges };
};

// Optimize node positions to create a more compact layout
// eslint-disable-next-line no-unused-vars
const optimizeCompactLayout = (nodes, edges, layoutDirection) => {
  // Group nodes by depth level
  const levels = new Map();
  nodes.forEach((node) => {
    const depth = node.data.nodePath
      ? Math.floor(node.data.nodePath.length / 2)
      : 0;
    if (!levels.has(depth)) {
      levels.set(depth, []);
    }
    levels.get(depth).push(node);
  });

  // Sort nodes within each level to minimize edge crossings
  levels.forEach((levelNodes, depth) => {
    if (depth === 0) return; // Skip root level

    levelNodes.sort((a, b) => {
      // Find parent positions
      const aParentEdge = edges.find((e) => e.target === a.id);
      const bParentEdge = edges.find((e) => e.target === b.id);

      if (aParentEdge && bParentEdge) {
        const aParent = nodes.find((n) => n.id === aParentEdge.source);
        const bParent = nodes.find((n) => n.id === bParentEdge.source);

        if (aParent && bParent) {
          return layoutDirection === "horizontal"
            ? aParent.position.y - bParent.position.y
            : aParent.position.x - bParent.position.x;
        }
      }
      return 0;
    });

    // Reposition nodes to be more compact while maintaining order
    if (levelNodes.length > 1) {
      const minSpacing = layoutDirection === "horizontal" ? 80 : 60;
      let currentPos = 0;

      levelNodes.forEach((node, index) => {
        if (layoutDirection === "horizontal") {
          node.position.y = currentPos;
          currentPos += minSpacing;
        } else {
          node.position.x = currentPos;
          currentPos += minSpacing;
        }
      });

      // Center the level
      const totalSpan = currentPos - minSpacing;
      const offset = -totalSpan / 2;

      levelNodes.forEach((node) => {
        if (layoutDirection === "horizontal") {
          node.position.y += offset;
        } else {
          node.position.x += offset;
        }
      });
    }
  });
};

export const nodePathToConditions = (nodePath) => {
  if (Array.isArray(nodePath)) {
    const conditions = [];
    const startIndex = nodePath[0] === "root" ? 1 : 0;
    for (let i = startIndex; i < nodePath.length - 1; i += 2) {
      conditions.push({
        tag_id: nodePath[i],
        values: [nodePath[i + 1]],
      });
    }
    if (nodePath.length === 1 && nodePath[0] !== "root") {
      conditions.push({
        tag_id: nodePath[0],
        values: [],
      });
    }
    return conditions;
  }

  const conditions = [];
  const pathParts = nodePath.split(">");
  const startIndex = pathParts[0] === "root" ? 1 : 0;
  for (let i = startIndex; i < pathParts.length - 1; i += 2) {
    conditions.push({
      tag_id: pathParts[i],
      values: [pathParts[i + 1]],
    });
  }
  if (pathParts.length === 2 && pathParts[0] !== "root") {
    conditions.push({
      tag_id: pathParts[0],
      values: [],
    });
  }
  return conditions;
};

export const getNewConditionsFromListOfStringConditions = (
  listOfStringConditions,
) => {
  if (!listOfStringConditions || listOfStringConditions.length === 0) {
    return null;
  }

  const conditions = {
    condition: "AND",
    children: [],
  };
  for (let i = 0; i < listOfStringConditions.length; i = i + 2) {
    const tagName = listOfStringConditions[i];
    if (i + 1 >= listOfStringConditions.length) {
      break;
    }
    const value = listOfStringConditions[i + 1];
    conditions.children.push({
      tag: {
        name: tagName,
        values: [value],
      },
    });
  }

  return conditions;
};

export const isEquivalent = (a, b) => {
  const aProps = [];
  const bProps = [];

  const isEqual = (a, b) => {
    if (aProps.indexOf(a) !== -1 && bProps.indexOf(b) !== -1) {
      return true;
    }

    if (a == null || b == null) {
      return a === b;
    }

    if (typeof a !== typeof b) {
      return false;
    }

    if (typeof a !== "object") {
      return a === b;
    }

    aProps.push(a);
    bProps.push(b);

    if (a.constructor !== b.constructor) {
      return false;
    }

    if (Array.isArray(a)) {
      if (a.length !== b.length) {
        return false;
      }
      for (let i = 0; i < a.length; i++) {
        if (!isEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => keysB.includes(key) && isEqual(a[key], b[key]));
  };

  return isEqual(a, b);
};

export const GRAPH_NODE_TYPES = {
  BINARY: "binary",
  DEFINED_VALUES: "defined_values",
  MIXED: "mixed",
  OPEN_ENDED: "open_ended",
  HIERARCHY: "hierarchy",
};
